import { useEffect, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import {
  Cloud,
  CloudOff,
  CircleHelp,
  Home,
  MoreHorizontal,
  Package,
  ShoppingCart,
  ReceiptText,
  Tags,
  WalletCards,
  type LucideIcon,
} from "lucide-react";
import { db } from "../lib/db";
import { flushSyncQueue } from "../lib/sync";
import { refreshFromServer } from "../lib/data";
import { useAuth } from "../lib/auth";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end: boolean;
  primary: boolean;
  desktopOnly?: boolean;
}

const operationalItems: NavItem[] = [
  { to: "/", label: "Trang chủ", icon: Home, end: true, primary: false },
  { to: "/products", label: "Sản phẩm", icon: Tags, end: false, primary: false, desktopOnly: true },
  { to: "/inventory", label: "Kho", icon: Package, end: false, primary: false },
  { to: "/sales", label: "Bán hàng", icon: ShoppingCart, end: false, primary: true },
  { to: "/cash", label: "Sổ tiền", icon: WalletCards, end: false, primary: false },
  { to: "/more", label: "Thêm", icon: MoreHorizontal, end: false, primary: false },
];

const accountantItems: NavItem[] = [
  { to: "/", label: "Trang chủ", icon: Home, end: true, primary: false },
  { to: "/products", label: "Sản phẩm", icon: Tags, end: false, primary: false, desktopOnly: true },
  { to: "/inventory", label: "Kho", icon: Package, end: false, primary: false },
  { to: "/sales/history", label: "Đơn hàng", icon: ReceiptText, end: false, primary: false },
  { to: "/cash", label: "Sổ tiền", icon: WalletCards, end: false, primary: false },
  { to: "/more", label: "Thêm", icon: MoreHorizontal, end: false, primary: false },
];

export function AppShell() {
  const auth = useAuth();
  const items =
    auth.session?.user.role === "ACCOUNTANT"
      ? accountantItems
      : operationalItems;
  const pending = useLiveQuery(
    async () => {
      const all = await db.syncQueue
        .where("status")
        .anyOf("PENDING", "FAILED", "SYNCING")
        .toArray();
      const tenantId = auth.session?.tenant?.id || "default";
      const storeId = auth.session?.store?.id || "default";
      return all.filter((item) => item.tenantId === tenantId && item.storeId === storeId).length;
    },
    [auth.session?.tenant?.id, auth.session?.store?.id],
    0,
  );
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);

  async function syncNow() {
    if (!navigator.onLine || auth.demo) return;
    setSyncing(true);
    try {
      const result = await flushSyncQueue(auth.session?.tenant?.id, auth.session?.store?.id);
      if (result.unauthorized) {
        auth.invalidateSession();
        return;
      }
      await refreshFromServer();
    } finally {
      setSyncing(false);
    }
  }

  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      void syncNow();
    };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [auth.demo]);

  return (
    <div className="app-frame">
      <aside className="desktop-sidebar" aria-label="Điều hướng chính">
        <div className="brand-block">
          <img src="/logo.png" alt="" />
          <div><strong>BLedger</strong><span>Sổ Kinh Doanh</span></div>
        </div>
        <nav>
          {items.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => isActive ? "active" : ""}>
              <Icon size={20} aria-hidden />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="app-column">
        <header className="topbar">
          <div className="topbar-brand">
            <img src="/logo.png" alt="" />
            <div>
              <strong>{auth.session?.store.name ?? "Sổ Kinh Doanh"}</strong>
              <span>{auth.demo ? "Dữ liệu mẫu" : "BLedger"}</span>
            </div>
          </div>
          <div className="topbar-actions">
            <Link className="help-chip" to="/help" aria-label="Hướng dẫn sử dụng BLedger">
              <CircleHelp size={16} aria-hidden />
              <span>Hướng dẫn</span>
            </Link>
            <button
              className="sync-chip"
              onClick={() => void syncNow()}
              disabled={!online || syncing || auth.demo}
              title={auth.demo ? "Demo cục bộ" : "Đồng bộ dữ liệu"}
            >
              {!online ? <CloudOff size={16} /> : <Cloud size={16} />}
              <span>{pending ? `Chờ đồng bộ ${pending}` : !online ? "Ngoại tuyến" : syncing ? "Đang đồng bộ" : "Đã đồng bộ"}</span>
            </button>
          </div>
        </header>

        {!online ? (
          <div className="offline-banner">
            {auth.session?.user.role === "ACCOUNTANT"
              ? "Đang ngoại tuyến — có thể xem dữ liệu đã lưu trên thiết bị. Dữ liệu mới sẽ cập nhật khi có mạng."
              : "Đang ngoại tuyến — vẫn có thể bán hàng. Dữ liệu sẽ tự đồng bộ khi có mạng."}
          </div>
        ) : null}

        <main className="app-content">
          <Outlet />
        </main>

        <nav className="bottom-nav" aria-label="Điều hướng chính">
          {items.filter((item) => !item.desktopOnly).map(({ to, label, icon: Icon, primary, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => `${isActive ? "active" : ""} ${primary ? "primary-nav" : ""}`}
            >
              <span className="nav-icon"><Icon size={primary ? 24 : 21} aria-hidden /></span>
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
